import { useState, useEffect, useRef, SyntheticEvent, FC } from 'react';
import { useIntl } from 'react-intl';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useStyles } from './simulator-tabs.styles';
import { TabPanel } from '../../../common/tab-panel';
import { SimulatorTabProps } from '../simulator.models';
import { SimulatorBenefit } from '../benefit/benefit';
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable';

export const SimulatorTabs: FC<{ tabs: SimulatorTabProps[] }> = ({ tabs }): JSX.Element => {
  const [value, setValue] = useState(0);
  const [circlePosition, setCirclePosition] = useState({ x: 150, y: 50 });
  const [circleSize, setCircleSize] = useState(150);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0, margin: 0 });
  const [circleOffset, setCircleOffset] = useState({ x: 0, y: 0 });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const classes = useStyles();
  const intl = useIntl();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl'));
  const isTablet = !isMobile && !isDesktop;

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  function a11yProps(index: number) {
    return {
      id: `tab-${index}`,
      'aria-controls': `tabpanel-${index}`,
    };
  }

  const handleDrag = (e: DraggableEvent, data: DraggableData) => {
    setCirclePosition({ x: data.x, y: data.y });
  };

  useEffect(() => {
    if (isMobile) {
      setCircleSize(200);
      setCanvasDimensions({ width: 425, height: 865, margin: 0 });
      setCircleOffset({ x: 80, y: 272 });
    } else if (isTablet) {
      setCircleSize(200);
      setCanvasDimensions({ width: 768, height: 733, margin: 0 });
      setCircleOffset({ x: 230, y: 246 });
    } else {
      setCircleSize(300);
      setCanvasDimensions({ width: 1440, height: 800, margin: 40 });
      setCircleOffset({ x: 565, y: 270 });
    }
  }, [isMobile, isTablet, isDesktop]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.src = value === 0 ? tabs[0].backgroundBlur : value === 1 ? tabs[1].backgroundBlur : tabs[2].backgroundBlur;
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(
        circlePosition.x + circleOffset.x,
        circlePosition.y + circleOffset.y,
        circleSize / 2,
        0,
        Math.PI * 2,
      );
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    };
  }, [circlePosition, circleSize, value, canvasDimensions]);

  return (
    <div className={classes.root}>
      <Tabs
        value={value}
        onChange={handleChange}
        indicatorColor="secondary"
        textColor="inherit"
        variant="fullWidth"
        aria-label="full width tabs example"
      >
        {tabs.map((tabItem, index) => (
          <Tab
            key={index}
            label={intl.formatMessage({ id: tabItem.name })}
            {...a11yProps(index)}
          />
        ))}
      </Tabs>
      {tabs.map((tabItem, index) => (
        <TabPanel value={value} index={index} key={index}>
          <div className={classes.wrapper}>
            <img
              src={tabItem.background}
              alt=""
              aria-hidden="true"
              className={classes.image}
            />
            <canvas
              ref={canvasRef}
              width={canvasDimensions.width}
              height={canvasDimensions.height}
              style={{
                position: 'absolute',
                top: 0,
                left: `${canvasDimensions.margin}px`,
                pointerEvents: 'none',
                zIndex: 2,
              }}
            />
            <Draggable
              bounds="parent"
              position={circlePosition}
              onDrag={handleDrag}
            >
              <div
                className={classes.draggableCircle}
              />
            </Draggable>
          </div>
          <div className={classes.panelInner}>
            <ul className={classes.list}>
              {tabItem.benefits.map(({ content, arrowPosition, position }, indexNumber) => (
                <li key={indexNumber}>
                  <SimulatorBenefit
                    content={content}
                    arrowPosition={arrowPosition}
                    position={position}
                  />
                </li>
              ))}
            </ul>
          </div>
        </TabPanel>
      ))}
    </div>
  );
};
